import { Dispatch, SetStateAction } from 'react';

import { MatchaMetaLink } from '@/components/MatchaMetaLink';
import { useTradeEngine } from '@/components/TransactionModule/useTradeEngine';

import { CHAIN_IDS } from '@/constants/chain';

import { useIntentsAvailability } from '@/hooks/useIntentsAvailability';
import { useCrossChainResolvedSlippage, useResolvedSlippage } from '@/hooks/useResolvedSlippage';

import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';
import {
  resetSettingsSelector,
  slippageSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { formatMatchaFeeRange } from '@/utils/fees';
import { routes } from '@/utils/routes';

import { MenuId } from '../../index';
import { FixedSettingItem } from './FixedSettingsList/FixedSettingItem';
import { LiquiditySourcesSection } from './LiquiditySourcesSection';
import { MatchaAutoAvailableSection } from './MatchaAutoAvailableSection';
import { MatchaAutoSwitchSection } from './MatchaAutoSwitchSection';
import { NetworkCostAmount } from './NetworkCostAmount';
import { SlippageMarketMode } from './Slippage';

const SeparatorLine = () => <div className="bg-special-border-divider w-full h-px my-6" />;

interface MainProps {
  /** Buy token */
  buyToken: TokenResult | undefined;
  /** ID of the current network. */
  chainId: number;
  /** Whether the active pair spans two chains. */
  isCrossChain: boolean;
  /** Sell token */
  sellToken: TokenResult | undefined;
  /** Callback that updates the current menu. */
  setCurrentMenu: Dispatch<SetStateAction<MenuId>>;
}

/**
 * Body of the main settings menu.
 */
export const Main = ({ buyToken, chainId, isCrossChain, sellToken, setCurrentMenu }: MainProps) => {
  // store
  const resetSettings = useMarketTradeSettingsStore(resetSettingsSelector);
  const { resetLiquiditySources } = useLiquiditySourcesStore(liquiditySourcesEventHandlers);
  const { quote } = useTradeEngine();
  const isIntentsEnabled = useIntentsAvailability({ buyToken, sellToken, chainId }).isEnabled;
  const { slippage, isSlippageCustom, slippageMode, setSettings } =
    useMarketTradeSettingsStore(slippageSelector);
  const isSolana = chainId === CHAIN_IDS.SOLANA;
  const isAutoLane = isIntentsEnabled && !isCrossChain;

  const marketSlippage = useResolvedSlippage({
    chainId,
    sellToken,
    buyToken,
    enabled: !isCrossChain,
  });
  const crossChainSlippage = useCrossChainResolvedSlippage({
    sellToken,
    buyToken,
    enabled: isCrossChain,
  });
  const resolvedSlippage = isCrossChain ? crossChainSlippage : marketSlippage;
  const autoSlippageBps =
    (!isSolana || isCrossChain) &&
    resolvedSlippage.isAuto &&
    resolvedSlippage.isReady &&
    sellToken &&
    buyToken
      ? resolvedSlippage.bps
      : undefined;
  const autoSlippageLabel =
    autoSlippageBps !== undefined ? `Auto · ${autoSlippageBps / 100}%` : undefined;
  const totalNetworkFee = quote.fee?.totalNetworkFee;

  const matchaFees = formatMatchaFeeRange(
    isCrossChain ? 'crossChain' : isSolana ? 'solana' : isAutoLane ? 'auto' : 'standard',
  );
  const feeInfoText =
    matchaFees === '0%'
      ? "Matcha doesn't charge a fee for this trade."
      : 'Fee applied on selected token pairs.';

  // event handlers
  const handleClick = () => {
    resetLiquiditySources();
    resetSettings();
  };

  const onSetSlippage = (value: number, isCustom: boolean) => {
    setSettings({
      slippage: value,
      isSlippageCustom: isCustom,
      slippageMode: 'manual',
    });
  };

  const onSelectAutoSlippage = () => {
    setSettings({ slippageMode: 'auto' });
  };

  return (
    <>
      <div className="max-h-[550px] tablet:max-h-full overflow-y-auto px-4 tablet:px-6 pb-8">
        <MatchaAutoSwitchSection
          buyToken={buyToken}
          chainId={chainId}
          isCrossChain={isCrossChain}
          sellToken={sellToken}
        />

        <MatchaAutoAvailableSection />

        <SlippageMarketMode
          slippage={slippage}
          isSlippageCustom={isSlippageCustom}
          setSlippage={onSetSlippage}
          slippageMode={slippageMode}
          onSelectAuto={onSelectAutoSlippage}
          autoLabel={autoSlippageLabel}
          autoSlippageBps={autoSlippageBps}
          tooltipContent={
            isCrossChain ? (
              'Slippage tolerance applied to each step (swap and bridge) of the cross-chain transaction.'
            ) : isAutoLane ? (
              <span>
                Allowable difference between the expected and executed prices of a trade. Matcha
                Auto provides full protection from frontrunning bots and MEV.&nbsp;
                <a
                  href={`${routes.BLOG}/article/what-is-mev`}
                  target="_blank"
                  className={linkStyles({ color: 'purple', underline: true })}
                >
                  Learn more
                </a>
              </span>
            ) : (
              'Allowable difference between the expected and executed prices of a trade. Your transaction will revert if price changes unfavorably by more than this percentage.'
            )
          }
        />
        <SeparatorLine />

        {!isSolana && !isCrossChain && (
          <>
            <LiquiditySourcesSection chainId={chainId} setCurrentMenu={setCurrentMenu} />
            <SeparatorLine />
          </>
        )}

        {!isCrossChain && (
          <>
            <FixedSettingItem
              leftLabel="Network cost"
              tooltipContent={
                isAutoLane
                  ? 'Matcha handles the gas, so you can trade without holding native tokens. Network costs are embedded into the trade.'
                  : 'Fee required in order to successfully conduct a transaction on the blockchain.'
              }
              showCheckmark={isAutoLane}
              rightContent={
                isAutoLane ? (
                  'Free'
                ) : (
                  <NetworkCostAmount
                    key={chainId}
                    chainId={chainId}
                    totalNetworkFee={totalNetworkFee}
                  />
                )
              }
            />
            <SeparatorLine />
          </>
        )}

        <FixedSettingItem
          leftLabel="Smart routing"
          tooltipContent="Routing automates the search for the most favorable price among various exchanges."
          rightContent="Auto"
        />
        <SeparatorLine />
        <FixedSettingItem
          leftLabel="Fee"
          tooltipContent={
            <div className="flex flex-col gap-1">
              <p>{feeInfoText}</p>
              {matchaFees !== '0%' && <MatchaMetaLink />}
            </div>
          }
          showCheckmark={false}
          rightContent={matchaFees}
        />
      </div>

      <div className="items-center flex space-between p-4 static bottom-0 left-0 right-0 z-10 border-t-1 border-special-border-divider">
        <button
          className={buttons({ color: 'transparent', shape: 'rounded', size: 'small' })}
          onClick={handleClick}
        >
          Reset defaults
        </button>
      </div>
    </>
  );
};
